/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import { pdf, Document, Page, Text, View, StyleSheet, Font, Image } from "@react-pdf/renderer";
import Rudaw from '../../assets/fonts/rudawregular2.ttf';
import { useTranslation } from "react-i18next";
import PostService from "../../services/postService";
import { formatPrice } from "../../utils";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { useCompany } from '../../provider/companyProvider';
import { fileApiRoute } from "../../services/networkService";

const registerFont = () => {
    Font.register({
        family: 'Rudaw',
        fonts: [{ src: Rudaw, fontWeight: 'normal' }],
    });
};

const usePdfPost = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language !== 'en';
    const [isLoading, setIsLoading] = useState(false);
    const { business } = useCompany();

    const styles = StyleSheet.create({
        row: {
            flexDirection: isRTL ? 'row-reverse' : 'row',
            alignItems: 'center',
            paddingHorizontal: 4
        },
        key: {
            fontWeight: 'bold',
            width: '40%',
            textAlign: isRTL ? 'right' : 'left',
            direction: isRTL ? 'rtl' : 'ltr',
            fontSize: 9
        },
        value: {
            flex: 1,
            textAlign: isRTL ? 'right' : 'left',
            paddingHorizontal: 2,
            fontSize: 9
        },
        barcode: {
            width: '50%',
            height: 52,
            alignSelf: 'center',
            marginVertical: 1
        },
        qrcode: {
            width: 60,
            height: 60,
            alignSelf: 'center',
        },
        footer: {
            textAlign: "center",
            fontSize: 8,
            marginTop: 5,
            color: "#000"
        }
    });

    const fetchPost = async (postId) => {
        try {
            setIsLoading(true);
            const data = await PostService.fetchOne(postId);
            return data;
        } catch (error) {
            console.error("Failed to fetch post:", error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const generateBarcode = (text) => {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, text, { format: "CODE128", displayValue: false });
        return canvas.toDataURL('image/png');
    };

    const generateQrCode = async (text) => {
        try {
            return await QRCode.toDataURL(text);
        } catch (err) {
            console.error('Error generating QR code:', err);
            return '';
        }
    };


    const generatePDF = async (postData) => {
        if (!postData) return;

        const barcodeImage = generateBarcode(postData.id);
        const qrCodeUrl = await generateQrCode(postData.id.toString());

        const MyDocument = () => (
            <Document>
                <Page size="A6" style={{
                    padding: 4,
                    direction: isRTL ? 'rtl' : 'ltr',
                    fontFamily: 'Rudaw',
                    height: "auto",
                    overflow: 'hidden'
                }}>
                    <View style={{
                        border: "1px solid #000",
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: 10,
                    }}>
                        <View style={{
                            display: 'flex',
                            direction: isRTL ? 'rtl' : 'ltr',
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                            justifyContent: 'space-between'
                        }}>
                            {/* qrcode */}
                            <View style={{
                                display: 'flex',
                                direction: isRTL ? 'rtl' : 'ltr',
                                flexDirection: isRTL ? 'row-reverse' : 'row',
                                width: '60%'
                            }}>
                                {qrCodeUrl && (
                                    <Image style={styles.qrcode} src={qrCodeUrl} />
                                )}

                                <View
                                    style={{
                                        marginVertical: 12,
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        alignItems: isRTL ? 'flex-end' : 'flex-start',
                                    }}
                                >
                                    <Text>{business?.name}</Text>
                                    <Text>{t('id')}: {postData.id}</Text>
                                    {/* <Text>{dayjs().format('DD/MM/YYYY HH:mm:ss')}</Text> */}
                                    <Text>{postData.createdAt}</Text>
                                </View>
                            </View>

                            <View style={{
                                width: '40%',
                            }}>
                                {/* logo */}
                                <Image style={{
                                    width: '65%',
                                    alignSelf: "center",
                                }} src={`${fileApiRoute}/${business.logo}`} />
                            </View>
                        </View>

                        <View style={{ borderBottomWidth: '1px', borderColor: "#000", marginBottom: 4 }} />

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('driver')}:</Text>
                            <Text style={styles.value}>{postData.driver?.name}</Text>
                        </View>

                        <View style={{ borderBottomWidth: '1px', borderColor: "#000", marginVertical: 4 }} />

                        {/* Address Info */}
                        <View style={styles.row}>
                            <Text style={styles.key}>{t('address')}:</Text>
                            <Text style={styles.value}>{`${postData.receiverAddress}, ${postData.street.name}, ${postData.street.city.name}`}</Text>
                        </View>

                        <View style={{ borderBottomWidth: '1px', borderColor: "#000", marginVertical: 4 }} />

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('receiverName')}:</Text>
                            <Text style={styles.value}>{postData.receiverName}</Text>
                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('receiverPhone')}:</Text>
                            <Text style={styles.value}>{postData.receiverPhone}, {postData.receiverPhoneTwo}</Text>
                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('postType')}:</Text>
                            <Text style={styles.value}>{postData.postType.name}</Text>
                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('totalPrice')}:</Text>
                            <Text style={styles.value}>{formatPrice(postData.totalPrice)}</Text>
                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('supervisor')}:</Text>
                            <Text style={styles.value}>{postData.driver && postData.driver.supervisor.name}</Text>
                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('note')}:</Text>
                            <Text style={{ ...styles.value, fontSize: 8 }}>
                                {(postData.note) ? `${(postData.note).slice(0, 136)}${(postData.note).length > 136 ? '...' : ''}` : '-'}
                            </Text>

                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={{ borderBottomWidth: '1px', borderColor: "#000", marginVertical: 4 }} />

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('pageName')}:</Text>
                            <Text style={styles.value}>{postData.page?.name}</Text>
                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={styles.row}>
                            <Text style={styles.key}>{t('pagePhone')}:</Text>
                            <Text style={styles.value}>{postData.page?.phone}</Text>
                        </View>
                        <View style={{ height: 3 }}></View>

                        <View style={{ borderBottomWidth: '1px', borderColor: "#000", marginVertical: 4 }} />
                        {/* Barcode */}
                        <Image style={styles.barcode} src={barcodeImage} />

                        <View style={{ borderBottomWidth: '1px', borderColor: "#000", marginVertical: 4 }} />
                        <Text style={{ paddingHorizontal: 4, fontSize: 9 }}>{business?.name}</Text>
                        <Text style={{ paddingHorizontal: 4, fontSize: 9 }}>{business?.addressOne}</Text>
                        <Text style={{ paddingHorizontal: 4, fontSize: 9 }}>{business?.addressTwo}</Text>
                        <Text style={{ paddingHorizontal: 4, fontSize: 9 }}>{business?.phoneOne} - {business?.phoneTwo}</Text>
                    </View>
                </Page>
            </Document>
        );

        try {
            const blob = await pdf(<MyDocument />).toBlob();
            return blob;
        } catch (error) {
            console.error("Error generating PDF:", error);
            return null;
        }
    };

    const pdfPost = async (postId) => {
        registerFont();
        const postData = await fetchPost(postId);
        if (!postData) return;
        const blob = await generatePDF(postData);
        if (blob) {
            const url = URL.createObjectURL(blob);
            window.open(url);
        }
    };

    return { isLoading, fetchPost, generatePDF, pdfPost };
};

export default usePdfPost;
